import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Fade,
  Tooltip,
  alpha,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { AuthContext } from "../../contexts/AuthContext";
import AddUserCard from "./SubUserManagement/AddUserCard";
import UserCard from "./SubUserManagement/UserCard";
import AddUserDialog from "./SubUserManagement/AddUserDialog";
import UserProfileDialog from "./SubUserManagement/UserProfileDialog";
import { useErrorDialog } from "../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../contexts/SuccessDialogContext";
import ComponentLoading from "../App/ComponentLoading";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import TransferOwnershipDialog from "./SubUserManagement/TransferOwnershipDialog";
import ManagerPanel from "./SubUserManagement/PermissionManager/ManagerPanel";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  getSubUserManagementDisplayConfig,
  shouldUseLargeSubUserManagement,
} from "../../utils/responsiveLayoutSettings";

export const USER_TYPE_CONFIG = {
  sub: {
    label: "Team Member",
    color: "primary",
    icon: PersonIcon,
    description: "Direct members of your organization",
    canTransferOwnership: true,
    canBulkEdit: true,
  },
  subsidiary: {
    label: "Subsidiary Member",
    color: "success",
    icon: CardGiftcardIcon,
    description:
      "Members are provided as a complimentary promotional user by Tax Savvy.",
    canTransferOwnership: false,
    canBulkEdit: true,
  },
};

const SubUserManagement = () => {
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [subUsers, setSubUsers] = useState([]);
  const [subsidiaryUsers, setSubsidiaryUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkPermissionModalOpen, setBulkPermissionModalOpen] = useState(false);
  const [transferOwnershipOpen, setTransferOwnershipOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [useLargeLayout, setUseLargeLayout] = useState(() =>
    shouldUseLargeSubUserManagement()
  );
  const {
    global_authState,
    registerSubUser,
    registerLocalSubUser,
    getSubUserList,
    getSubsidiaryUserList,
    updateSubUserRole,
    updateSubUserName,
    removeSubUser,
    updateSubsidiaryUserInfo,
    terminateSubsidiaryUser,
    getCompanyList,
    getCompanyPermission,
    assignPermissionToTaxComp,
    assignUsersToCompanyPermission,
    getCompanyUserPermissionList,
    transferOwnership,
    getCompanyWithTaxComputationYears,
  } = useContext(AuthContext);

  const showError = useErrorDialog();
  const showSuccess = useSuccessDialog();
  const isTaxProfessionalPlan =
    global_authState?.subscription?.plan_name === "Tax Professional";
  const isSsoOrganization = global_authState?.auth_provider === "entra";
  const displayConfig = useMemo(
    () => getSubUserManagementDisplayConfig(useLargeLayout),
    [useLargeLayout]
  );

  useEffect(() => {
    fetchSubUsers();
  }, [getSubUserList, getCompanyList]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateLayoutMode = () => {
      setUseLargeLayout(shouldUseLargeSubUserManagement());
    };

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);
    window.visualViewport?.addEventListener("resize", updateLayoutMode);

    return () => {
      window.removeEventListener("resize", updateLayoutMode);
      window.visualViewport?.removeEventListener("resize", updateLayoutMode);
    };
  }, []);

  const ensureArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.items)) return value.items;
    return [];
  };

  const fetchSubUsers = async () => {
    setLoading(true);
    try {
      const users = await getSubUserList();
      const subsidiaries = await getSubsidiaryUserList();
      const normalizedUsers = ensureArray(users);
      const normalizedSubsidiaries = ensureArray(subsidiaries);
      const sortedUsers = normalizedUsers.sort((a, b) =>
        a.status === "pending" ? 1 : -1
      );

      setSubUsers(sortedUsers);
      setSubsidiaryUsers(normalizedSubsidiaries);
      const companies = await getCompanyWithTaxComputationYears();
      setCompanies(companies?.data || []);
    } catch (error) {
      showError(error);
    }
    setLoading(false);
  };

  const allUsers = useMemo(() => {
    const normalizedSubUsers = ensureArray(subUsers).map((u) => ({
      ...u,
      source: "sub",
    }));

    const normalizedSubsidiaryUsers = ensureArray(subsidiaryUsers).map((u) => ({
      ...u,
      source: "subsidiary",
    }));

    return [...normalizedSubUsers, ...normalizedSubsidiaryUsers];
  }, [subUsers, subsidiaryUsers]);

  const handleProfileOpen = async (user) => {
    setSelectedUser(user);
    setProfileOpen(true);
  };

  const handleProfileClose = (refresh = false) => {
    setProfileOpen(false);
    setSelectedUser(null);

    if (refresh) {
      fetchSubUsers();
    }
  };

  const handleUserUpdated = (updatedUser) => {
    if (!updatedUser?.u_id) return;

    const mergeUser = (user) =>
      user.u_id === updatedUser.u_id
        ? {
            ...user,
            ...updatedUser,
            user_auth: {
              ...(user.user_auth || {}),
              ...(updatedUser.user_auth || {}),
            },
          }
        : user;

    setSubUsers((prev) => prev.map(mergeUser));
    setSubsidiaryUsers((prev) => prev.map(mergeUser));
    setSelectedUser((prev) => (prev?.u_id === updatedUser.u_id ? mergeUser(prev) : prev));
  };

  const handleAddUser = async (userData) => {
    const { inviteType, ...payload } = userData;
    try {
      setOpen(false);
      setLoading(true);
      if (inviteType === "local") {
        await registerLocalSubUser(payload);
        showSuccess("Password invitation sent");
      } else {
        await registerSubUser(payload);
        showSuccess("Sub-user invited successfully");
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMessage = error?.response?.data?.message;
      const knownEmailConflict =
        serverMessage === "Email already in use" ||
        serverMessage ===
          "Email is already registered or pending in another organization.";
      if (status === 409 && serverMessage && !knownEmailConflict) {
        showError(serverMessage);
      } else if (status === 409)
        showError(
          "The email address provided is already registered in the system. Please use a different email address to send the invitation."
        );
      else showError(error);
    } finally {
      setLoading(false);
      fetchSubUsers();
    }
  };

  const handleAssignPermission = async (permissionData) => {
    try {
      setLoading(true);
      await assignPermissionToTaxComp(permissionData);
      showSuccess("Permissions updated successfully");
      // Refresh permissions after update
      if (permissionData.target_u_id) {
        await getUserPermission(permissionData.target_u_id);
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignUsersToCompany = async ({ c_id, items }) => {
    try {
      setLoading(true);
      await assignUsersToCompanyPermission({ c_id, items });
      showSuccess("Company permissions updated successfully");
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const getUserPermission = async (target_u_id) => {
    try {
      const permissionList = await getCompanyPermission(target_u_id);

      return permissionList;
    } catch (error) {
      showError(error);
      return {};
    }
  };

  const getCompanyUserPermissions = async () => {
    try {
      const list = await getCompanyUserPermissionList();
      return list;
    } catch (error) {
      showError(error);
      return [];
    }
  };

  const filteredUsers = useMemo(() => {
    const term = (searchTerm || "").toLowerCase().trim();
    return allUsers.filter((u) => {
      const status = u.status || u.user_auth?.status;
      const matchesSearch =
        !term ||
        (u.name && u.name.toLowerCase().includes(term)) ||
        (u.email && u.email.toLowerCase().includes(term));
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [allUsers, searchTerm, statusFilter]);

  const usersBySource = useMemo(() => {
    const sub = filteredUsers.filter((u) => u.source === "sub");
    const subsidiary = filteredUsers.filter((u) => u.source === "subsidiary");
    return { sub, subsidiary };
  }, [filteredUsers]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        height: "100%",
        bgcolor: "grey.50",
      }}
    >
      {/* Header - CompanyList style */}
      <Box
        sx={{
          width: "100%",
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(12px)",
          px: displayConfig.header.px,
          py: displayConfig.header.py,
          mb: displayConfig.header.mb,
          boxShadow: "0 2px 16px rgba(0, 0, 0, 0.06)",
          borderBottom: "1px solid",
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.25),
          position: "relative",
          overflow: "visible",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -1,
            left: 0,
            right: 0,
            height: "2px",
            background:
              "linear-gradient(90deg, transparent, #603F97 30%, #D31D83 70%, transparent)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
              py: { xs: 0.25, sm: 0 },
              mr: displayConfig.header.mr,
            }}
          >
            <Box
              sx={{
                width: displayConfig.header.iconSize,
                height: displayConfig.header.iconSize,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #603F97 0%, #D31D83 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 4px 10px rgba(96, 63, 151, 0.25)",
              }}
            >
              <GroupIcon sx={{ color: "white", fontSize: 22 }} />
            </Box>
            <Box>
              <Typography
                variant='h5'
                sx={{
                  fontWeight: 700,
                  background:
                    "linear-gradient(45deg, #603F97 30%, #D31D83 90%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: displayConfig.header.titleFontSize,
                  lineHeight: 1.2,
                }}
              >
                User Management
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 0.25 }}
              >
                {searchTerm || statusFilter !== "all"
                  ? `${filteredUsers.length} of ${allUsers.length} member${allUsers.length !== 1 ? "s" : ""}`
                  : `${allUsers.length} member${allUsers.length !== 1 ? "s" : ""}`}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: displayConfig.header.controlsGap,
              alignItems: { xs: "stretch", sm: "center" },
              minWidth: 0,
              flex: { sm: 1 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.25,
                minWidth: 0,
                flex: { sm: 1 },
              }}
            >
              <TextField
                variant='outlined'
                size='small'
                placeholder='Search by name or email...'
                value={searchTerm}
                onChange={(e) => {
                  // You can adjust the max length below as needed.
                  const maxLength = 40;
                  setSearchTerm(e.target.value.slice(0, maxLength));
                }}
                inputProps={{
                  maxLength: 40, // Set your desired maximum length here.
                }}
                InputProps={{
                  sx: { "& input": { py: 0.875, fontSize: "0.875rem" } },
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon sx={{ color: "grey.500", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment
                      position='end'
                      sx={{ cursor: "pointer" }}
                      onClick={() => setSearchTerm("")}
                    >
                      <Tooltip title='Clear search' arrow>
                        <Box sx={{ p: 0.25 }}>
                          <CloseIcon
                            sx={{
                              color: "grey.400",
                              fontSize: 16,
                              "&:hover": { color: "primary.main" },
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  maxWidth: displayConfig.header.searchMaxWidth,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: "white",
                    pr: 1,
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                      },
                    },
                    "&.Mui-focused": {
                      boxShadow: (theme) =>
                        `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                      borderColor: "primary.main",
                    },
                  },
                }}
              />

              <FormControl size='small' sx={{ minWidth: 110 }}>
                <InputLabel sx={{ fontSize: "0.8125rem" }}>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  size='small'
                  label='Status'
                  sx={{
                    borderRadius: 1.5,
                    backgroundColor: "white",
                    fontSize: "0.8125rem",
                    "& .MuiSelect-select": { py: 0.875 },
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                      },
                    },
                    "&.Mui-focused": {
                      boxShadow: (theme) =>
                        `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  <MenuItem value='all'>All</MenuItem>
                  <MenuItem value='active'>Active</MenuItem>
                  <MenuItem value='pending'>Pending</MenuItem>
                  <MenuItem value='banned'>Banned</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1.25,
                flexWrap: "wrap",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Tooltip title='Transfer primary account ownership' arrow>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<SwapHorizIcon sx={{ fontSize: 18 }} />}
                  onClick={() => setTransferOwnershipOpen(true)}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  Change Primary Owner
                </Button>
              </Tooltip>
              {global_authState?.user_role === "admin" &&
                isTaxProfessionalPlan && (
                  <Tooltip title='Manage permissions in bulk' arrow>
                    <Button
                      variant='contained'
                      size='small'
                      startIcon={
                        <AdminPanelSettingsIcon sx={{ fontSize: 18 }} />
                      }
                      onClick={() => setBulkPermissionModalOpen(true)}
                      sx={{
                        borderRadius: 1.5,
                        textTransform: "none",
                        boxShadow: (theme) =>
                          `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
                      }}
                    >
                      Permission Manager
                    </Button>
                  </Tooltip>
                )}
            </Box>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <ComponentLoading />
      ) : (
        <Fade in timeout={320}>
          <Box sx={{ width: "100%" }}>
            {filteredUsers.length === 0 && allUsers.length > 0 ? (
              <Box
                sx={{
                  px: 3,
                  py: 4,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 200,
                }}
              >
                <Typography variant='body1' color='text.secondary'>
                  No members match your search or filters. Try different terms
                  or clear filters.
                </Typography>
              </Box>
            ) : (
              ["sub", "subsidiary"].map((source) => {
                const config = USER_TYPE_CONFIG[source];
                const users = usersBySource[source];
                const showSection = users.length > 0 || source === "sub";
                if (!showSection) return null;

                return (
                  <Box
                    key={source}
                    sx={{
                      px: displayConfig.section.px,
                      py: displayConfig.section.py,
                      mb: source === "sub" ? 0 : displayConfig.section.mb,
                    }}
                  >
                    <Box sx={{ mb: displayConfig.section.mb }}>
                      <Typography
                        variant='h6'
                        sx={{
                          fontWeight: 600,
                          fontSize: displayConfig.section.titleFontSize,
                        }}
                      >
                        {config.label}s
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {config.description}
                      </Typography>
                    </Box>
                    <Grid container spacing={displayConfig.grid.spacing}>
                      {source === "sub" && (
                        <Grid item {...displayConfig.cardGridItem}>
                          <AddUserCard
                            displayConfig={displayConfig.addCard}
                            onClick={() => setOpen(true)}
                          />
                        </Grid>
                      )}
                      {users.map((user, index) => (
                        <Grid
                          item
                          {...displayConfig.cardGridItem}
                          key={user.u_id ?? user.email ?? index}
                        >
                          <UserCard
                            displayConfig={displayConfig.card}
                            user={user}
                            typeConfig={config}
                            onClick={() => handleProfileOpen(user)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                );
              })
            )}
          </Box>
        </Fade>
      )}

      <AddUserDialog
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleAddUser}
        allowLocalInvite={isSsoOrganization}
      />

      <UserProfileDialog
        open={profileOpen}
        onClose={handleProfileClose}
        user={selectedUser}
        isTaxProfessionalPlan={isTaxProfessionalPlan}
        onRoleChange={updateSubUserRole}
        onNameChange={updateSubUserName}
        onUpdateSubsidiaryUser={updateSubsidiaryUserInfo}
        onTerminateSubsidiaryUser={terminateSubsidiaryUser}
        onDeleteUser={removeSubUser}
        onUserUpdated={handleUserUpdated}
        onRefreshUsers={() => fetchSubUsers()}
        showSuccess={showSuccess}
        showError={showError}
      />

      <ManagerPanel
        user_role={global_authState?.user_role}
        user_type={global_authState?.user_type}
        user_id={global_authState?.uid}
        isSubscribed={
          isTaxProfessionalPlan
        }
        onGetPermission={getUserPermission}
        onAssignPermission={handleAssignPermission}
        onAssignUsersToCompany={handleAssignUsersToCompany}
        onGetCompanyUserPermissionList={getCompanyUserPermissions}
        bulkPermissionModalOpen={bulkPermissionModalOpen}
        setBulkPermissionModalOpen={setBulkPermissionModalOpen}
        subUsers={subUsers}
        subsidiaries={subsidiaryUsers}
        companies={companies}
      />

      <TransferOwnershipDialog
        open={transferOwnershipOpen}
        onClose={() => {
          setTransferOwnershipOpen(false);
        }}
        subUsers={subUsers}
        onTransfer={transferOwnership}
        showError={showError}
        showSuccess={showSuccess}
      />
    </Box>
  );
};

export default SubUserManagement;
